import { useEffect, useMemo, useState } from "react";
import type { ConversationSummary } from "@/lib/store";
import { useStore } from "@/lib/store";
import { useTheme } from "@/lib/theme";
import { navigate as navigateRoute } from "@/lib/router";
import { searchChats, type ChatSearchHit } from "@/lib/api";
import { ConfirmDialog } from "../ConfirmDialog";
import { ChatRow } from "./sidebar/ChatRow";
import { FolderHeader } from "./sidebar/FolderHeader";

const FOLDERS_ROOT_KEY = "__folders_root__";

const FOOTER_BUTTON = "flex size-8 items-center justify-center rounded-md bg-muted/60 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors";

export function Sidebar() {
  const { theme, setTheme } = useTheme();
  const cycleTheme = () => setTheme(theme === "light" ? "dark" : theme === "dark" ? "system" : "light");
  const themeIcon = theme === "light" ? "fa-sun" : theme === "dark" ? "fa-moon" : "fa-circle-half-stroke";

  const conversations = useStore((s) => s.conversations);
  const activeId = useStore((s) => s.activeId);
  const loadingChatId = useStore((s) => s.loadingChatId);
  const activeRunChatIds = useStore((s) => s.activeRunChatIds);
  const activeRunSet = useMemo(() => new Set(activeRunChatIds), [activeRunChatIds]);
  const selectConversation = useStore((s) => s.selectConversation);
  const startNewChat = useStore((s) => s.startNewChat);
  const deleteConversation = useStore((s) => s.deleteConversation);
  const deleteAllConversations = useStore((s) => s.deleteAllConversations);
  const setSidebarOpen = useStore((s) => s.setSidebarOpen);
  const setShowApiKey = useStore((s) => s.setShowApiKey);

  const onClose = () => setSidebarOpen(false);
  const onSelect = async (id: string) => {
    await selectConversation(id);
    navigateRoute(`/chat/${id}`);
  };
  const onNew = () => {
    startNewChat();
    navigateRoute("/");
    setSidebarOpen(false);
  };
  const onDelete = async (id: string) => {
    await deleteConversation(id);
    if (activeId === id) navigateRoute("/");
  };
  const [confirmingDeleteAll, setConfirmingDeleteAll] = useState(false);
  const onDeleteAll = () => setConfirmingDeleteAll(true);
  const onShowTools = () => navigateRoute("/tools");
  const onShowLibrary = () => navigateRoute("/library");
  const onShowSkills = () => navigateRoute("/skills");
  const onShowMemory = () => navigateRoute("/memory");
  const onShowApiKey = () => setShowApiKey(true);

  const folders = useStore((s) => s.folders);
  const createFolder = useStore((s) => s.createFolder);
  const renameFolder = useStore((s) => s.renameFolder);
  const deleteFolder = useStore((s) => s.deleteFolder);
  const moveChat = useStore((s) => s.moveChat);
  const togglePin = useStore((s) => s.togglePin);
  const collapsed = useStore((s) => s.settings.collapsedFolders);
  const updateSettings = useStore((s) => s.updateSettings);

  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [query, setQuery] = useState("");
  const searching = query.trim().length > 0;
  const [searchHits, setSearchHits] = useState<ChatSearchHit[]>([]);
  const [searchPending, setSearchPending] = useState(false);
  useEffect(() => {
    if (!searching) { setSearchHits([]); setSearchPending(false); return; }
    setSearchPending(true);
    const q = query;
    const t = setTimeout(async () => {
      try {
        const hits = await searchChats(q);
        if (query === q) setSearchHits(hits);
      } finally {
        if (query === q) setSearchPending(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [query, searching]);
  const toggleFolder = (id: string) => {
    const current = collapsed[id] ?? true;
    const next = { ...collapsed, [id]: !current };
    void updateSettings({ collapsedFolders: next });
  };

  const grouped = useMemo(() => {
    const pinned: ConversationSummary[] = [];
    const unfiled: ConversationSummary[] = [];
    const byFolder = new Map<string, ConversationSummary[]>();
    for (const c of conversations) {
      if (c.pinned) {
        pinned.push(c);
      } else if (c.folderId) {
        if (!byFolder.has(c.folderId)) byFolder.set(c.folderId, []);
        byFolder.get(c.folderId)!.push(c);
      } else {
        unfiled.push(c);
      }
    }
    return { pinned, unfiled, byFolder };
  }, [conversations]);

  const foldersCollapsed = collapsed[FOLDERS_ROOT_KEY] ?? true;
  const toggleFoldersRoot = () => {
    void updateSettings({ collapsedFolders: { ...collapsed, [FOLDERS_ROOT_KEY]: !foldersCollapsed } });
  };

  const submitNewFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { setCreatingFolder(false); return; }
    try {
      await createFolder(name);
      setNewFolderName("");
      setCreatingFolder(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleRename = async (id: string, name: string) => {
    try { await renameFolder(id, name); }
    catch (e) { console.error(e); }
  };

  const handleDeleteFolder = async (id: string) => {
    try { await deleteFolder(id); }
    catch (e) { console.error(e); }
  };

  const chatRow = (conv: ConversationSummary, indented = false) => (
    <ChatRow
      key={conv.id}
      conv={conv}
      active={conv.id === activeId}
      loading={conv.id === loadingChatId}
      streaming={activeRunSet.has(conv.id)}
      folders={folders}
      indented={indented}
      onSelect={() => onSelect(conv.id)}
      onDelete={() => onDelete(conv.id)}
      onMove={(folderId) => moveChat(conv.id, folderId)}
      onTogglePin={(pinned) => togglePin(conv.id, pinned)}
    />
  );

  return (
    <div className="sidebar-root flex h-full w-full flex-col border-r bg-card">
      <div
        data-tauri-drag-region
        className="flex items-center justify-between border-b px-3 py-3"
        style={{ paddingTop: "calc(0.75rem + var(--titlebar-h, 0px))" }}
      >
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground" title="Hide sidebar">
            <i className="fa-solid fa-sidebar" />
          </button>
          <h2 className="text-sm font-semibold">Chats</h2>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCreatingFolder((s) => !s)}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
            title="New folder"
          >
            <i className="fa-solid fa-folder-plus" />
          </button>
          {conversations.length > 0 && (
            <button
              onClick={onDeleteAll}
              className="rounded-md p-1.5 text-muted-foreground hover:text-destructive"
              title="Delete all chats"
            >
              <i className="fa-solid fa-trash-can" />
            </button>
          )}
          <button onClick={onNew} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground" title="New chat">
            <i className="fa-solid fa-plus" />
          </button>
        </div>
      </div>
      <div className="border-b px-2 py-1.5">
        <div className="relative">
          <i className="fa-solid fa-magnifying-glass absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats & messages"
            className="w-full rounded-md border bg-background pl-7 pr-7 py-1 text-xs outline-none focus:border-primary"
          />
          {searching && (
            <button
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            >
              <i className="fa-solid fa-xmark text-[10px]" />
            </button>
          )}
        </div>
      </div>
      <div className="sidebar-scroll flex-1 overflow-y-auto">
        {creatingFolder && (
          <div className="flex items-center gap-2 border-b bg-muted/30 px-2 py-1.5">
            <i className="fa-solid fa-folder-plus text-xs text-muted-foreground" />
            <input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onBlur={submitNewFolder}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitNewFolder();
                if (e.key === "Escape") { setNewFolderName(""); setCreatingFolder(false); }
              }}
              placeholder="Folder name"
              className="flex-1 rounded border bg-background px-2 py-0.5 text-xs outline-none focus:border-primary"
            />
          </div>
        )}
        {searching ? (
          <>
            {searchHits.map((hit) => {
              const conv: ConversationSummary = {
                id: hit.id,
                title: hit.title,
                systemPrompt: hit.systemPrompt,
                folderId: hit.folderId,
                pinned: hit.pinned,
                createdAt: hit.createdAt,
                updatedAt: hit.updatedAt,
              };
              return (
                <div key={hit.id}>
                  {chatRow(conv)}
                  {hit.matchedIn === "content" && hit.snippet && (
                    <p className="border-b border-border/50 bg-muted/20 px-3 pb-2 pt-0 text-[11px] text-muted-foreground line-clamp-2">
                      <i className="fa-solid fa-quote-left mr-1.5 text-[9px]" />
                      {hit.snippet}
                    </p>
                  )}
                </div>
              );
            })}
            {!searchPending && searchHits.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matching chats</p>
            )}
            {searchPending && searchHits.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                <i className="fa-solid fa-spinner-third fa-spin mr-1.5" />Searching…
              </p>
            )}
          </>
        ) : (
          <>
            {grouped.pinned.length > 0 && (
              <div className="border-b border-primary/20 bg-primary/[0.03]">
                <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-primary/80">
                  <i className="fa-solid fa-thumbtack text-[9px]" />
                  <span>Pinned</span>
                </div>
                {grouped.pinned.map((conv) => chatRow(conv))}
              </div>
            )}
            {grouped.unfiled.map((conv) => chatRow(conv))}
            {folders.length > 0 && (
              <div className="border-t">
                <button
                  onClick={toggleFoldersRoot}
                  className="flex w-full items-center gap-1.5 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
                >
                  <i className={`fa-solid fa-chevron-${foldersCollapsed ? "right" : "down"} text-[9px]`} />
                  <i className="fa-solid fa-folder text-xs" />
                  <span>Folders</span>
                  <span className="ml-auto normal-case opacity-60">{folders.length}</span>
                </button>
                {!foldersCollapsed && folders.map((folder) => {
                  const isCollapsed = collapsed[folder.id] ?? true;
                  const items = grouped.byFolder.get(folder.id) ?? [];
                  return (
                    <div key={folder.id}>
                      <FolderHeader
                        folder={folder}
                        collapsed={!!isCollapsed}
                        onToggle={() => toggleFolder(folder.id)}
                        onRename={(name) => handleRename(folder.id, name)}
                        onDelete={() => handleDeleteFolder(folder.id)}
                        onDropChat={(chatId) => moveChat(chatId, folder.id)}
                      />
                      {!isCollapsed && items.map((conv) => chatRow(conv, true))}
                    </div>
                  );
                })}
              </div>
            )}
            {conversations.length === 0 && folders.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">No conversations yet</p>
            )}
          </>
        )}
      </div>
      <div className="border-t px-3 py-2 flex items-center gap-1">
        <button
          onClick={onShowTools}
          title="Tools"
          className={FOOTER_BUTTON}
        >
          <i className="fa-solid fa-screwdriver-wrench" />
        </button>
        <button
          onClick={onShowLibrary}
          title="Library"
          className={FOOTER_BUTTON}
        >
          <i className="fa-solid fa-folder-tree" />
        </button>
        <button
          onClick={onShowSkills}
          title="Skills"
          className={FOOTER_BUTTON}
        >
          <i className="fa-solid fa-wand-magic-sparkles" />
        </button>
        <button
          onClick={onShowMemory}
          title="Memory"
          className={FOOTER_BUTTON}
        >
          <i className="fa-solid fa-brain" />
        </button>
        <button
          onClick={onShowApiKey}
          title="API key"
          className={FOOTER_BUTTON}
        >
          <i className="fa-solid fa-key" />
        </button>
        <button
          onClick={cycleTheme}
          title={`Theme: ${theme}`}
          className={`ml-auto ${FOOTER_BUTTON}`}
        >
          <i className={`fa-solid ${themeIcon}`} />
        </button>
      </div>
      {confirmingDeleteAll && (
        <ConfirmDialog
          title="Delete all conversations?"
          message="This will permanently remove every chat. Folders are kept."
          confirmLabel="Delete all"
          destructive
          onConfirm={async () => {
            setConfirmingDeleteAll(false);
            await deleteAllConversations();
            navigateRoute("/");
          }}
          onCancel={() => setConfirmingDeleteAll(false)}
        />
      )}
    </div>
  );
}
